/* The Onboarding app's cases and role templates, from Blueprint's side.
 *
 * A sibling of GraphClient, like TrainingScopeClient, and for the same reason: GraphClient is
 * bound to Blueprint's OWN site and these lists live on another one (HirdOnboarding).
 *
 * WHAT BLUEPRINT DOES HERE, AND DOES NOT
 *
 * It reads the role templates, because they decide what the Add a joiner flow offers. It reads
 * the cases, to show a person's status (With HR, With Accounts, Complete). And it opens one case,
 * when IT hands a joiner over. It never changes a case after that: HR and Accounts own it. The
 * contract, columns included, is in docs/joiner-onboarding.md.
 *
 * Case rows are matched in memory rather than with a filter. A column Graph will not filter on
 * without an override header is not a thing to build a hand-over on, and the list is short: one
 * row per joiner.
 */

import { GraphError, type TokenProvider } from '../sharepoint/GraphClient';
import type { IJoinerCase, IOpenCase, IOpenedCase, IRoleTemplate, JoinerStage } from '../../models';
import type { IJoinerService } from '../IServices';

const GRAPH = 'https://graph.microsoft.com/v1.0';

export interface IJoinerSite {
  siteId: string;
  casesList: string;
  templatesList: string;
  /** Onboarding_Settings: who is emailed at each handoff (notify.hr, notify.accounts, notify.complete). */
  settingsList?: string;
}

type Fields = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/** A SharePoint date or date-time as the date part only. */
const day = (v: unknown): string => (typeof v === 'string' ? v.slice(0, 10) : '');

export function toTemplate(id: string, f: Fields): IRoleTemplate {
  let apps: IRoleTemplate['apps'] = {};
  let kit: string[] = [];
  try {
    const a = JSON.parse(str(f.AppsJson) || '{}') as unknown;
    if (a && typeof a === 'object' && !Array.isArray(a)) apps = a as IRoleTemplate['apps'];
  } catch { /* a template with unreadable access is a template with none */ }
  try {
    const k = JSON.parse(str(f.KitJson) || '[]') as unknown;
    if (Array.isArray(k)) kit = k.filter((x): x is string => typeof x === 'string');
  } catch { /* as above */ }
  return {
    id,
    title: str(f.Title),
    jobTitle: str(f.JobTitle) || undefined,
    depot: str(f.Depot) || undefined,
    department: str(f.Department) || undefined,
    apps,
    trainingPack: str(f.TrainingPack) || undefined,
    inductionPack: str(f.InductionPack) || undefined,
    kit,
  };
}

const STAGES: JoinerStage[] = ['HR', 'Accounts', 'Complete'];

export function toCase(id: string, f: Fields): IJoinerCase {
  const stage = str(f.Stage) as JoinerStage;
  return {
    id,
    name: str(f.Title),
    upn: str(f.Upn),
    graphId: str(f.EntraId) || undefined,
    templateId: str(f.TemplateId) || undefined,
    jobTitle: str(f.JobTitle),
    depot: str(f.Depot),
    department: str(f.Department),
    start: day(f.StartDate),
    stage: STAGES.includes(stage) ? stage : 'HR',
    handedToHrOn: str(f.HandedToHrOn),
    hrFinishedOn: str(f.HrFinishedOn) || undefined,
    accountsFinishedOn: str(f.AccountsFinishedOn) || undefined,
  };
}

export class JoinerClient implements IJoinerService {
  constructor(
    private readonly getToken: TokenProvider,
    private readonly site: IJoinerSite,
    private readonly scopes: string[] = ['Sites.ReadWrite.All'],
  ) {}

  private async request<T>(url: string, init: RequestInit = {}): Promise<T> {
    const token = await this.getToken(this.scopes);
    const res = await fetch(url.startsWith('http') ? url : `${GRAPH}${url}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...init.headers,
      },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      const detail = (body as { error?: { message?: string } } | null)?.error?.message;
      throw new GraphError(
        detail ? `Graph ${res.status}: ${detail}` : `Graph ${res.status} on ${url}`,
        res.status,
        body,
      );
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  private items(list: string): Promise<{ value: { id: string; fields: Fields }[] }> {
    return this.request(
      `/sites/${this.site.siteId}/lists/${encodeURIComponent(list)}/items?$expand=fields&$top=999`,
    );
  }

  async templates(): Promise<IRoleTemplate[]> {
    const r = await this.items(this.site.templatesList);
    return r.value
      .map((i) => toTemplate(i.id, i.fields))
      .filter((t) => t.title)
      .sort((a, b) => a.title.localeCompare(b.title));
  }

  async cases(): Promise<IJoinerCase[]> {
    const r = await this.items(this.site.casesList);
    return r.value.map((i) => toCase(i.id, i.fields));
  }

  async openCase(input: IOpenCase): Promise<IOpenedCase> {
    /* Idempotent on the sign-in name, so a Hand to HR that failed AFTER opening the case (a slow
       reply, a dropped connection) and is pressed again finds the case rather than opening a
       second one for the same person. */
    const existing = (await this.cases()).find(
      (c) => c.upn.toLowerCase() === input.upn.toLowerCase(),
    );
    if (existing) return { ...existing, created: false };

    const created = await this.request<{ id: string; fields: Fields }>(
      `/sites/${this.site.siteId}/lists/${encodeURIComponent(this.site.casesList)}/items`,
      {
        method: 'POST',
        body: JSON.stringify({
          fields: {
            Title: input.name,
            Upn: input.upn,
            EntraId: input.graphId ?? '',
            TemplateId: input.templateId ?? '',
            JobTitle: input.jobTitle,
            Depot: input.depot,
            Department: input.department,
            StartDate: input.start,
            Stage: 'HR',
            HandedToHrOn: new Date().toISOString(),
            /* Only when there is one: a column the site has not been provisioned with is refused with a 400
               for the WHOLE row, so a case would fail to open over an optional field. */
            ...(input.welcomeTo && input.welcomeTo.trim() ? { WelcomeTo: input.welcomeTo.trim() } : {}),
          },
        }),
      },
    );
    return { ...toCase(created.id, created.fields ?? {}), created: true };
  }

  /** The addresses set under notify.hr in the Onboarding settings. Nobody set, or the list unreadable, is nobody:
   *  mail is never a reason for a hand-over to fail. */
  async hrRecipients(): Promise<string[]> {
    if (!this.site.settingsList) return [];
    try {
      const r = await this.items(this.site.settingsList);
      const row = r.value.find((i) => str(i.fields.Title) === 'notify.hr');
      return str(row?.fields.Value)
        .split(/[;,\n]+/)
        .map((a) => a.trim())
        .filter((a) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(a));
    } catch {
      return [];
    }
  }
}
